import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 20, bottom: 20, left: 70 };
const ROW_HEIGHT = 28;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '8px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '4px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function GanttTimeline({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const sortedStates = useMemo(() => {
    if (!data || !data.states || data.states.length === 0) return [];
    const sorted = [...data.states];
    sorted.sort((a, b) => {
      const aTotal = a.segments.reduce((sum, s) => sum + s.changeCount, 0);
      const bTotal = b.segments.reduce((sum, s) => sum + s.changeCount, 0);
      return bTotal - aTotal;
    });
    return sorted;
  }, [data]);

  useEffect(() => {
    if (!sortedStates || sortedStates.length === 0 || !data || !data.dateRange) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(500, containerWidth);
    const height = sortedStates.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = sortedStates.length * ROW_HEIGHT;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Time scale
    const timeStart = new Date(data.dateRange.start + 'T00:00:00');
    const timeEnd = new Date(data.dateRange.end + 'T00:00:00');

    const x = d3.scaleTime()
      .domain([timeStart, timeEnd])
      .range([0, innerW]);

    // Y scale
    const y = d3.scaleBand()
      .domain(sortedStates.map((d) => d.state))
      .range([0, innerH])
      .padding(0.15);

    // Vertical grid lines
    const xTicks = x.ticks();
    g.selectAll('.grid-v')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Horizontal grid lines (between rows)
    sortedStates.forEach((d, i) => {
      if (i > 0) {
        const rowY = y(d.state);
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', rowY)
          .attr('y2', rowY)
          .attr('stroke', '#f3f4f6')
          .attr('stroke-width', 1);
      }
    });

    // X axis (top - date ticks)
    g.append('g')
      .call(
        d3.axisTop(x)
          .ticks(d3.timeWeek.every(1))
          .tickSize(0)
          .tickFormat(d3.timeFormat('%b %d'))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis (left - state labels)
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', '500');

    // Draw segments for each state
    sortedStates.forEach((stateData) => {
      const rowY = y(stateData.state);
      const barHeight = y.bandwidth();

      stateData.segments.forEach((seg) => {
        const segStart = new Date(seg.startDate + 'T00:00:00');
        const segEnd = new Date(seg.endDate + 'T00:00:00');
        const segX = x(segStart);
        const segW = Math.max(1, x(segEnd) - x(segStart));

        const isChanged = seg.status === 'changed';
        const fillColor = isChanged ? '#fee2e2' : '#dcfce7';
        const strokeColor = isChanged ? '#fca5a5' : '#bbf7d0';

        const segGroup = g.append('g')
          .style('cursor', 'pointer');

        // Segment rectangle
        segGroup.append('rect')
          .attr('x', segX)
          .attr('y', rowY)
          .attr('width', segW)
          .attr('height', barHeight)
          .attr('fill', fillColor)
          .attr('stroke', strokeColor)
          .attr('stroke-width', 1)
          .attr('rx', 2);

        // Change count badge inside changed segments
        if (isChanged && seg.changeCount > 0 && segW > 16) {
          const badgeCx = segX + segW / 2;
          const badgeCy = rowY + barHeight / 2;

          segGroup.append('text')
            .attr('x', badgeCx)
            .attr('y', badgeCy)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('font-weight', 600)
            .attr('fill', '#dc2626')
            .text(seg.changeCount);
        }

        // Invisible hover target
        segGroup.append('rect')
          .attr('x', segX)
          .attr('y', rowY)
          .attr('width', segW)
          .attr('height', barHeight)
          .attr('fill', 'transparent')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state: stateData.state,
              addressCount: stateData.addressCount,
              startDate: seg.startDate,
              endDate: seg.endDate,
              status: seg.status,
              changeCount: seg.changeCount,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedStates, data]);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Change Timeline</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No timeline data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Change Timeline</div>
        <div style={styles.subtitle}>
          State-level periods of price stability and change
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{ width: 12, height: 10, background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 1 }} />
            <span>Stable</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ width: 12, height: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 1 }} />
            <span>Changed</span>
          </div>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Period</span>
            <span style={{ fontWeight: 600 }}>{tooltip.startDate} — {tooltip.endDate}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Status</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.status === 'changed' ? '#dc2626' : '#16a34a',
            }}>
              {tooltip.status === 'changed' ? 'Changed' : 'Stable'}
            </span>
          </div>
          {tooltip.changeCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#dc2626' }}>Changes</span>
              <span style={{ fontWeight: 600, color: '#dc2626' }}>{tooltip.changeCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
